import { Link } from "react-router-dom"
import { Button, buttonVariants } from "./ui/button"
import { CircleUserRound } from "lucide-react"
import { format } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import axios from "axios"
import { PrescriptionIcon } from "../features/patient-panel/prescriptions"
import { toast } from "./ui/use-toast"
import { base64PDFToBlobUrl, cn } from "../lib/utils"
import { fetchHealthRecordsByBookingId, rescheduleAppointment, cancelAppointment } from "../services/patients"
import BackIcon from "./back-icon"
import { useDoctorSlots } from "../lib/hooks/doctors/use-doctor-slots"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { useNavigate } from "react-router-dom"

const MAX_SLOTS = 6
const today = format(new Date(), "dd-MM-yyyy")

function AppointmentDetails({
  doctor,
  data,
  branchDetails,
}: {
  doctor:
  | {
    fullName: string
    profilePhoto?: string
    specialities?: string[]
    subDistrictOrCity?: string
    fee?: string
    clinicName?: string
    state?: string
  }
  | undefined
  data: {
    slotDate?: Date
    dayOfWeek?: string
    slotTiming?: string
    symptoms?: string[]
    patient?: { name: string }
    bookingId?: number
  } | null

  branchDetails?: {
    branchId: number
    clinicName: string
    subDistrictOrCity: string
    state: string
    address: string
    pincode: string
    latitude: string
    longitude: string
  }[]
}) {
  const [healthRecords, setHealthRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<number | undefined>()
  const [selectedDate, setSelectedDate] = useState(today)
  const [tempSelectedSlot, setTempSelectedSlot] = useState<{ from: string; to: string } | null>(null)
  const [slotInfo, setSlotInfo] = useState("")
  const [currentBookingSlot, setCurrentBookingSlot] = useState<{ from: string; to: string } | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false)

  // New state variables for reschedule modal
  const [originalSlot, setOriginalSlot] = useState<{ from: string; to: string } | null>(null)
  const [originalDate, setOriginalDate] = useState<string>("")
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)

  const navigate = useNavigate()

  const parseSlotInfo = (slotInfoString: string) => {
    if (!slotInfoString || slotInfoString === "Slot information unavailable") {
      console.log("🚫 slotInfoString is empty or unavailable:", slotInfoString);
      return null;
    }
  
    // slotInfo format: "08:00 am - 08:15 am, Thursday, 17 Jul 2025"
    //console.log("🔍 Parsing slotInfo:", slotInfoString);
    const parts = slotInfoString.split(', ');
   // console.log("🔍 Split parts:", parts);
  
    if (parts.length < 3) {
     // console.log("🚫 Invalid slotInfo format, parts length < 3:", parts);
      return null;
    }
  
    const datePart = parts[2]; // "17 Jul 2025"
    const timePart = parts[0]; // "08:00 am - 08:15 am"
    //console.log("🔍 Date part:", datePart, "Time part:", timePart);
  
    // Convert date to dd-MM-yyyy format
    try {
      const parsedDate = new Date(datePart);
     // console.log("🔍 Parsed date:", parsedDate);
      if (isNaN(parsedDate.getTime())) {
        //console.log("🚫 Invalid date format:", datePart);
        return null;
      }
      const formattedDate = format(parsedDate, "dd-MM-yyyy");
      //console.log("🔍 Formatted date:", formattedDate);
  
      // Parse time slot
      if (timePart.includes(' - ')) {
        const [from, to] = timePart.split(' - ');
        //console.log("🔍 Parsed time slot: from", from.trim(), "to", to.trim());
        return {
          date: formattedDate,
          slot: { from: from.trim(), to: to.trim() }
        };
      } else {
        //console.log("🚫 Invalid time format:", timePart);
        return null;
      }
    } catch (error) {
      console.error("🚫 Error parsing slot info:", error);
      return null;
    }
  };

  useEffect(() => {
    //console.log("🔄 Setting up original booking details", { data })

    if (data?.slotDate && data?.dayOfWeek && data?.slotTiming) {
      const formattedDate = format(data.slotDate, "d MMM yyyy")
     // const info = `${formattedDate}, ${data.dayOfWeek}, ${data.slotTiming}`
     const info = `${data.slotTiming}, ${data.dayOfWeek}, ${formattedDate}`
      setSlotInfo(info)

      // Set original booking details
      const originalFormattedDate = format(data.slotDate, "dd-MM-yyyy")
      setOriginalDate(originalFormattedDate)
      //console.log("📅 Original date set:", originalFormattedDate)

      // Parse the original slot timing
      if (data.slotTiming && data.slotTiming.includes(' - ')) {
        const [from, to] = data.slotTiming.split(' - ')
        const originalSlotData = { from: from.trim(), to: to.trim() }
        setOriginalSlot(originalSlotData)
        //console.log("⏰ Original slot set:", originalSlotData)
      } else {
        //console.log("🚫 Could not parse original slot timing:", data.slotTiming)
      }
    } else {
      setSlotInfo("Slot information unavailable")
    }
  }, [data])

  const branch = branchDetails?.[0]

  const { slots = {}, isLoading: isSlotsLoading, isFetched: isSlotsFetched } = useDoctorSlots("2", selectedBranch)

  const selectedDateSlots = useMemo(() => {
    const slotsForDate = slots[selectedDate] ?? []
    //console.log("📊 Slots for selected date", selectedDate, ":", slotsForDate)
    return slotsForDate
  }, [slots, selectedDate])

  const [paginatedSlots, setPaginatedSlots] = useState<typeof selectedDateSlots>([])

  useEffect(() => {
    const getHealthRecords = async () => {
      if (!data?.bookingId || !data?.patient?.name) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch by bookingId
        const recordsByBookingId = await fetchHealthRecordsByBookingId(data.bookingId);
        // Optionally fetch by name and mobile if mobile is availabley
        // Combine or choose records based on your needs
        setHealthRecords(recordsByBookingId);
      } catch (err: any) {
        //setError(err.message || "Failed to fetch health records. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    getHealthRecords();
  }, [data?.bookingId, data?.patient?.name]);

  function handleCancelAppointment(bookingId: number | undefined) {
    if (!bookingId) {
      return toast({
        title: "Error",
        description: "Booking ID is missing.",
        variant: "destructive",
      });
    }

    cancelAppointment(bookingId)
      .then(() => {
        toast({
          title: "Success",
          description: "Your appointment has been successfully canceled.",
          variant: "default",
        });
        navigate("/doctors");
      })
      .catch((error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      });
  }

  function handleViewPrescription(prescription: string) {
    if (!prescription)
      return toast({
        title: "Consolidated report not found",
        variant: "destructive",
      })

    const mimeType = prescription.split(",")[0].split(":")[1].split(";")[0]
    const blobUrl = base64PDFToBlobUrl(mimeType, prescription.split(",")[1])

    window.open(blobUrl)
  }

  function handleSelectSlot(slot: { from: string; to: string }) {
    //console.log("🎯 Selecting slot:", slot)
    setTempSelectedSlot(slot)
  }

  function handleOpenRescheduleModal() {
    //console.log("🔄 Opening reschedule modal");

    setSelectedBranch(branch?.branchId);

    // Parse current booking details from slotInfo
    const parsed = parseSlotInfo(slotInfo);

    if (parsed) {
      setSelectedDate(parsed.date);
      setCurrentBookingSlot(parsed.slot);
      //console.log("📅 Setting current booking.", parsed);
    } else {
      setSelectedDate(originalDate || today);
      setCurrentBookingSlot(null);
      // console.log("⚡ Fallback.");
    }

    setShowRescheduleModal(true);
  }



  function handleCancelReschedule() {
    //console.log("❌ Cancelling reschedule")
    setShowRescheduleModal(false)
    setTempSelectedSlot(null)
    setSelectedDate(today)
  }

  function handleConfirmReschedule() {
    // console.log("✅ Confirming reschedule with:",
    //  {
    //   bookingId: data?.bookingId,
    //   selectedDate,
    //   tempSelectedSlot
    // });

    if (!data?.bookingId || !tempSelectedSlot) {
      return toast({
        title: "Error",
        description: "Booking ID or selected slot is missing.",
        variant: "destructive",
      });
    }

    // Parse current booking from slotInfo for accurate comparison
    const currentBooking = parseSlotInfo(slotInfo);

    // Prevent confirming the same slot and date
    if (currentBooking &&
      selectedDate === currentBooking.date &&
      tempSelectedSlot.from === currentBooking.slot.from &&
      tempSelectedSlot.to === currentBooking.slot.to) {
      return toast({
        title: "No Change",
        description: "Please select a different date or slot to reschedule.",
        variant: "destructive",
      });
    }

    const [day, month, year] = selectedDate.split("-").map(Number);
    const jsDate = new Date(year, month - 1, day);

    const parseTime = (time: string) => {
      const [hourMinute, period] = time.split(" ");
      let [hours, minutes] = hourMinute.split(":").map(Number);
      if (period.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (period.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }
      return { hours, minutes };
    };

    const { hours: startHours, minutes: startMinutes } = parseTime(tempSelectedSlot.from);
    const { hours: endHours, minutes: endMinutes } = parseTime(tempSelectedSlot.to);

    const appointmentDate = jsDate.toISOString();
    const startTime = new Date(year, month - 1, day, startHours, startMinutes).toISOString();
    const endTime = new Date(year, month - 1, day, endHours, endMinutes).toISOString();

    const payload = {
      bookingId: data.bookingId,
      appointmentDate,
      startTime,
      endTime,
    };

    //console.log("📤 Sending reschedule payload:", payload);

    rescheduleAppointment(payload)
      .then(() => {
        //console.log("✅ Appointment rescheduled successfully");
        toast({
          title: "Success",
          description: "Your appointment has been successfully rescheduled.",
          variant: "default",
        });

        const formattedDate = format(jsDate, "d MMM yyyy");
        const newSlotInfo = `${formattedDate}, ${data?.dayOfWeek || "Unknown Day"}, ${tempSelectedSlot.from} - ${tempSelectedSlot.to}`;
        setSlotInfo(newSlotInfo);

        setTempSelectedSlot(null);
        setShowRescheduleModal(false);

        window.location.reload();
      })
      .catch((error) => {
        //console.error("❌ Error rescheduling appointment:", error);
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred while rescheduling.",
          variant: "destructive",
        });
      });
  }
  function normalizeTime(timeString: string): string {
    const trimmed = timeString.trim().toLowerCase();
    // Remove leading zero from hours (08:30 am -> 8:30 am)
    return trimmed.replace(/^0(\d):/, '$1:');
  }

  useEffect(() => {
    if (selectedDateSlots.length) {
      setPaginatedSlots(selectedDateSlots.slice(0, MAX_SLOTS))
    }
  }, [selectedDateSlots])

  useEffect(() => {
    if (data?.slotDate && !selectedDate) {
      setSelectedDate(format(new Date(data.slotDate), "dd-MM-yyyy"))
    }
  }, [data?.slotDate, selectedDate])

  // Updated slot selection useEffect
  useEffect(() => {
    //console.log("🔹 Updating pagination");

    if (
      isSlotsFetched &&
      showRescheduleModal &&
      selectedDate &&
      slots[selectedDate]?.length
    ) {
      setPaginatedSlots(slots[selectedDate].slice(0, MAX_SLOTS));
      if (currentBookingSlot) {
        //console.log("Current booking to match against.", currentBookingSlot);

        // The parsed booking might differ from the currentBookingSlot
        const currentBooking = parseSlotInfo(slotInfo);

        // console.log("Parsed booking.", currentBooking);
        // console.log("Selected Date.", selectedDate);

        if (currentBooking) {
          if (selectedDate === currentBooking.date) {
            // console.log("Dates match.");

            // console.log("Looking for matching slot in slots.");
            slots[selectedDate].forEach((item, idx) => {
              // console.log(
              //   `Slot ${idx} -> from [${item.from.trim().toLowerCase()}] to [${item.to.trim().toLowerCase()}]`
              // );
            });

            // console.log(
            //   "We want to match from [%s] to [%s]",
            //   currentBookingSlot.from.trim().toLowerCase(),
            //   currentBookingSlot.to.trim().toLowerCase()
            // );

            const matching = slots[selectedDate].find((item) =>
              normalizeTime(item.from) === normalizeTime(currentBookingSlot.from) &&
              normalizeTime(item.to) === normalizeTime(currentBookingSlot.to)
            );

            if (matching) {
              // console.log("🎉 Auto-selected matching slot.", matching);
              setTempSelectedSlot(matching);
            } else {
              //console.log("🚥 Booking's slot not present.");

              // Fallback: we still want to show the booking's own slot
              setTempSelectedSlot(currentBookingSlot);
            }
          } else {
            //console.log("Dates do not match.");
            setTempSelectedSlot(null);
          }
        } else {
          //console.log("parseSlotInfo returned null.");
          setTempSelectedSlot(null);
        }
      } else {
        console.log("currentBookingSlot is null.");
      }





    }
  }, [isSlotsFetched, showRescheduleModal, selectedDate, slots, currentBookingSlot]);



  return (
    <>
      <div className="flex items-center gap-4 bg-teal-700 p-4 sm:justify-center shadow-md mb-4">
        <svg
          width="54"
          height="54"
          viewBox="0 0 54 54"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="animate-pulse"
        >
          <path
            d="M27 49.5C39.375 49.5 49.5 39.375 49.5 27C49.5 14.625 39.375 4.5 27 4.5C14.625 4.5 4.5 14.625 4.5 27C4.5 39.375 14.625 49.5 27 49.5Z"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.4375 26.9998L23.805 33.3673L36.5625 20.6323"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="text-lg font-bold text-primary-foreground">
          Dear {data?.patient?.name}, your appointment is booked
        </p>
      </div>
      <div className="container py-4 bg-gray-50 min-h-screen">
        {/* Doctor Details */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex gap-3">
            <Link
              to={`/doctors`}
              className={`${buttonVariants({
                variant: "ghost",
                size: "icon",
              })} group relative hover:bg-gray-100 transition-colors`}
            >
              <BackIcon />
              <span className="absolute left-1/2 top-full mt-1 hidden -translate-x-1/2 items-center rounded bg-gray-800 p-1 text-xs text-white shadow-lg group-hover:flex">
                Back
              </span>
            </Link>

            {doctor?.profilePhoto ? (
              <img
                src={`${doctor?.profilePhoto}`}
                alt={doctor?.fullName}
                className="size-16 rounded-full object-contain sm:size-24 border-2 "
              />
            ) : (
              <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-muted sm:size-20 border-2 ">
                <CircleUserRound className="size-full" strokeWidth={1} />
              </div>
            )}

            <div className="w-full space-y-4">
              <div>
                <h3 className="text-sm font-bold sm:text-xl">Dr {doctor?.fullName}</h3>
                {doctor?.specialities?.length && (
                  <p className="mb-1 text-xs text-muted-foreground sm:text-base">
                    {doctor?.specialities.join(", ")}
                  </p>
                )}
                <p className="mb-1 text-xs font-bold text-muted-foreground sm:text-base">
                  {doctor?.subDistrictOrCity}
                </p>
                <p className="mb-2 text-sm  font-semibold sm:text-base">
                  Fee: <span className="">₹{doctor?.fee ? doctor?.fee : "NA"}</span>
                </p>

                {/* <div className="flex justify-end m-2">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className={buttonVariants({
                      className: "shadow-md hover:shadow-lg transition-shadow px-2 py-1 text-xs", // Even smaller
                      variant: "secondary",
                    })}
                  >
                    Cancel appointment
                  </button>
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {data?.slotDate && data.dayOfWeek && data.slotTiming && (
  <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 className="text-base font-bold mb-1">Appointment Time</h3>
        <p className="text-sm font-bold text-primary mb-3 sm:mb-0">{slotInfo}</p>
      </div>
      <div className="flex flex-row gap-3 justify-end">
        <Button
          onClick={() => setIsModalOpen(true)}
          className={buttonVariants({
            variant: "outline",
            
            className: "shadow-md hover:shadow-lg  text-teal-700 font-semibold transition-shadow px-3 py-1 text-sm sm:text-sm",
          })}
        >
          Cancel appointment
        </Button>
        <Button
          onClick={handleOpenRescheduleModal}
          className={buttonVariants({
            variant: "outline",
            className: `                 
              text-teal-700          
              font-bold               
              border                  
              border-teal-700    
              shadow-md hover:shadow-lg
              transition-shadow
              px-3 py-1
              text-sm sm:text-sm
            `,
          })}
          
        >
          Reschedule
        </Button>
      </div>
    </div>
  </div>
)}

        {/* Cancel Appointment Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <h2 className="text-lg font-bold ">Confirm Cancellation</h2>
              <p className="mt-2 text-sm">
                Are you sure you want to cancel this appointment? This action cannot be undone.
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className={buttonVariants({
                    variant: "ghost",
                    className: "hover:bg-gray-100",
                  })}
                >
                  No
                </button>
                <button
                  onClick={() => handleCancelAppointment(data?.bookingId)}
                  className={buttonVariants({
                    variant: "default",
                    className: "bg-teal-700 hover:bg-teal-900",
                  })}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reschedule Modal */}
        {showRescheduleModal && selectedDateSlots.length > 0 && (
          <div className="mt-4 bg-white rounded-lg shadow-sm p-1 mb-6">
            <h3 className="mb-4 text-lg font-bold text-primary-2">Available Slots</h3>
            <div className="flex flex-wrap gap-4">
              <Tabs
                value={selectedDate}
                onValueChange={(val) => {
                  //console.log("📅 Date changed to:", val)
                  setSelectedDate(val)
                }}
                className="w-full"
              >
                <TabsList className="mb-4 h-[unset] w-full justify-center overflow-x-auto rounded-none border-b-2 p-0">
                  {Object.keys(slots)
                    .filter((slot) => {
                      const [day, month, year] = slot.split("-").map(Number);
                      const slotDate = new Date(year, month - 1, day);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      // Parse current booking date to always include it
                      const currentBooking = parseSlotInfo(slotInfo);
                      const isCurrentBookingDate = currentBooking && slot === currentBooking.date;

                      return slotDate >= today || isCurrentBookingDate;
                    })
                    .sort((a, b) => {
                      const todayStr = format(new Date(), "dd-MM-yyyy");

                      if (a === todayStr) return -1;
                      if (b === todayStr) return 1;

                      const [dayA, monthA, yearA] = a.split("-").map(Number);
                      const [dayB, monthB, yearB] = b.split("-").map(Number);

                      const dateA = new Date(yearA, monthA - 1, dayA);
                      const dateB = new Date(yearB, monthB - 1, dayB);

                      return dateA.getTime() - dateB.getTime();
                    })
                    .slice(0, 5) // <- take maximum first 5
                    .map((slot) => {
                      const todayStr = format(new Date(), "dd-MM-yyyy");

                      const [day, month, year] = slot.split("-").map(Number);
                      const jsDate = new Date(year, month - 1, day);
                      const isToday = slot === todayStr;
                      const dateStr = isToday ? "Today" : format(jsDate, "d MMM");

                      return (
                        <TabsTrigger
                          key={slot}
                          value={slot}
                          className="rounded-t-md !border-0 text-sm font-semibold text-foreground data-[state=active]:bg-primary-2 data-[state=active]:text-primary-foreground transition-colors"
                        >
                          {dateStr}
                        </TabsTrigger>
                      )
                    })}
                </TabsList>

                <TabsContent value={selectedDate} className="flex flex-col items-center">
                  {paginatedSlots.length && isSlotsFetched ? (
                    <>
                      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 w-full">
                        {paginatedSlots.map((slot) => {
                          const isSelected = tempSelectedSlot &&
                            tempSelectedSlot.from.trim() === slot.from.trim() &&
                            tempSelectedSlot.to.trim() === slot.to.trim();

                          // Updated original slot check using parsed slotInfo
                          const isOriginalSlot = (() => {
                            const currentBooking = parseSlotInfo(slotInfo);
                            return currentBooking &&
                              selectedDate === currentBooking.date &&
                              currentBooking.slot.from.trim() === slot.from.trim() &&
                              currentBooking.slot.to.trim() === slot.to.trim();
                          })();


                          const now = new Date()
                          const [day, month, year] = selectedDate.split("-").map(Number)
                          const [time, period] = slot.from.split(" ")
                          const [startHour, startMinute] = time.split(":").map(Number)
                          const hour24 =
                            period.toLowerCase() === "pm" && startHour !== 12
                              ? startHour + 12
                              : period.toLowerCase() === "am" && startHour === 12
                                ? 0
                                : startHour

                          const slotStartTime = new Date(year, month - 1, day, hour24, startMinute)
                          const isSlotPassed = slotStartTime < now

                          return (
                            <Button
                              key={slot.from}
                              variant="outline"
                              size="sm"
                              disabled={isSlotPassed}
                              className={cn(
                                "rounded-full border-2 text-xs lowercase hover:border-primary-2 transition-colors",
                                {
                                  "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground": isSelected,
                                  "border-orange-500 !bg-orange-50 font-bold !text-orange-600": isOriginalSlot && !isSelected,
                                  "opacity-50 cursor-not-allowed": isSlotPassed,
                                },
                              )}
                              onClick={() => !isSlotPassed && handleSelectSlot(slot)}
                            >
                              {slot.from} - {slot.to}
                              {isOriginalSlot && <span className="ml-1 text-xs">(Current)</span>}
                            </Button>
                          )
                        })}
                      </div>

                      {/* Updated button logic */}


                      {selectedDateSlots.length > MAX_SLOTS && paginatedSlots.length < selectedDateSlots.length && (
                        <Button
                          className="mt-7 text-xs font-bold text-primary underline"
                          variant="link"
                          onClick={() => setPaginatedSlots(selectedDateSlots)}
                        >
                          See all slots
                        </Button>

                      )}
                      {(tempSelectedSlot || showRescheduleModal) && (
                        <div className="mt-6 w-full flex justify-center gap-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full border-gray-300 hover:bg-gray-50"
                            onClick={handleCancelReschedule}
                          >
                            Cancel
                          </Button>
                          {/* Only show Confirm button if user selected a different slot/date */}
                          {/* Only show Confirm button if user selected a different slot/date */}
                          {(() => {
                            const currentBooking = parseSlotInfo(slotInfo);
                            // console.log("📅 Selected date:", slotInfo);
                            // console.log("🔍 Current booking details:", currentBooking);
                            const isDifferentSelection = currentBooking && tempSelectedSlot && (
                              selectedDate !== currentBooking.date ||
                              (tempSelectedSlot.from !== currentBooking.slot.from ||
                                tempSelectedSlot.to !== currentBooking.slot.to)
                            );

                            return isDifferentSelection && (
                              <Button
                                size="sm"
                                className="rounded-full bg-primary-2 hover:bg-primary-2/90 shadow-md"
                                onClick={handleConfirmReschedule}
                              >
                                Confirm Reschedule
                              </Button>
                            );
                          })()}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="pb-28 text-muted-foreground">No slots available for this date</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="mb-2 text-left font-bold sm:text-xl">For - {data?.patient?.name}</h2>
          <p className="text-sm">{data?.symptoms?.join(", ") ?? "No symptoms provided"}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="mb-2 text-left font-bold  sm:text-xl">At - {branch?.clinicName}</h2>
          <p className="text-sm">{branch?.subDistrictOrCity}</p>
          {branch?.state && <p className="text-sm">{branch.state}</p>}
          {branch?.address && <p className="text-sm mt-2">{branch.address}</p>}
          {branch?.pincode && <p className="text-sm">{branch.pincode}</p>}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="mb-3 text-left font-bold  sm:text-xl">Uploaded health records</h2>
          {loading && <p className="text-muted-foreground">Loading health records...</p>}
          {!loading && healthRecords?.every((record) => record.uploadRecords.length === 0) ? (
            <p className="text-muted-foreground">No health records</p>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {healthRecords.map((record) => (
                <div
                  key={record.recordId}
                  className="flex flex-col gap-2 rounded-md border p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <p className="font-medium">Consulted with {record.doctorNameOrLabName}</p>
                  <div className="flex flex-wrap gap-2">
                    {record.uploadRecords.map((upload: any, index: number) => (
                      <div
                        key={index}
                        className="flex cursor-pointer flex-col items-center rounded-md border p-3 shadow-sm hover:shadow-md transition-shadow bg-gray-50 max-w-[150px]"
                        onClick={() => handleViewPrescription(upload.reportData)}
                      >
                        <PrescriptionIcon />
                        <p className="font-bold text-gray-600 text-center truncate w-full">
                          {upload.reportName}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default AppointmentDetails